import { PlusIcon } from "lucide-react";
import { Button } from "../ui/buttons/button";
import { Input } from "../ui/input";
import { Label } from "../ui/shadcn/label";
import { useState } from "react";
import JSZip from "jszip";
import {
  computeBbox,
  parseKmlContent,
  parseGeoJsonContent,
  buildKmzResourceMap,
} from "../../utils/geo";
import {
  useImportedLayers,
  ImportedLayerFormat,
} from "../../context/ImportedLayersContext";
import * as shapefile from "shapefile";

const FileUpload = () => {
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const { addImportedLayer } = useImportedLayers();

  const convertFileToStream = async (
    file: File
  ): Promise<ReadableStream<Uint8Array>> => {
    const arrayBuffer = await file.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    const blob = new Blob([uint8Array]);
    return blob.stream();
  };

  // Verify if file is GeoJSON, KML, or Shapefile and convert accordingly
  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const files: FileList | null = event.target.files;
    if (!(files && files.length > 0)) return;

    setUploadError(null);
    setFileName(files[0].name);
    const file: File = files[0];
    const fileNameLower = file.name.toLowerCase();

    const reader = new FileReader();
    const isKmz = fileNameLower.endsWith(".kmz");
    const isZip = fileNameLower.endsWith(".zip");
    reader.onload = async (e: ProgressEvent<FileReader>) => {
      try {
        // KMZ (zipped KML) handling
        if (isKmz) {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const zip = await JSZip.loadAsync(arrayBuffer);
          const kmlFile = Object.values(zip.files).find((f: any) =>
            /\.kml$/i.test(f.name)
          );
          if (!kmlFile) {
            setUploadError("KMZ inválido: nenhum arquivo KML encontrado");
            setFileName(null);
            return;
          }
          const kmlText = await kmlFile.async("text");
          const resourceMap = await buildKmzResourceMap(zip);
          const kmlResult = parseKmlContent(kmlText, resourceMap, kmlFile.name);
          if (!kmlResult.ok) {
            setUploadError(kmlResult.error);
            setFileName(null);
            return;
          }
          const {
            doc: kmlDoc,
            geojson: geojsonMeta,
            safeForPlugin,
          } = kmlResult;
          const id = `imported-${Date.now()}-${Math.random()}`;
          addImportedLayer({
            id,
            name: file.name,
            format: "kml" as ImportedLayerFormat,
            featureCount: geojsonMeta.features.length,
            bbox: computeBbox(geojsonMeta),
            geojson: geojsonMeta,
            rawKml: kmlDoc,
            kmlSafeForPlugin: safeForPlugin,
            isVisible: true,
          });
          return;
        }
        // ZIP (shapefile.zip containing .shp/.dbf/.shx/.prj)
        if (isZip) {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const zip = await JSZip.loadAsync(arrayBuffer);
          const files = Object.values(zip.files) as any[];
          const shpEntry = files.find((f) => /\.shp$/i.test(f.name));
          const dbfEntry = files.find((f) => /\.dbf$/i.test(f.name));
          const cpgEntry = files.find((f) => /\.cpg$/i.test(f.name));
          const prjEntry = files.find((f) => /\.prj$/i.test(f.name));
          const qmdEntry = files.find((f) => /\.qmd$/i.test(f.name));
          const shxEntry = files.find((f) => /\.shx$/i.test(f.name));

          if (!shpEntry) {
            setUploadError("ZIP inválido: nenhum arquivo .shp encontrado");
            setFileName(null);
            return;
          }

          if (!dbfEntry) {
            setUploadError(
              "ZIP inválido: arquivo .dbf ausente (atributos podem faltar)"
            );
            setFileName(null);
            return;
          }

          const shpBuffer = await shpEntry.async("arraybuffer");
          const dbfBuffer = await dbfEntry.async("arraybuffer");
          const cpgText = cpgEntry ? await cpgEntry.async("text") : undefined;
          const prjText = prjEntry ? await prjEntry.async("text") : undefined;
          const qmdText = qmdEntry ? await qmdEntry.async("text") : undefined;
          const shxBuffer = shxEntry
            ? await shxEntry.async("arraybuffer")
            : undefined;
          const encoding = cpgText ? cpgText.trim() : undefined;

          const source = await shapefile.open(
            shpBuffer,
            dbfBuffer,
            encoding ? { encoding } : undefined
          );
          let features: any[] = [];
          let result = await source.read();
          while (!result.done) {
            features.push(result.value);
            result = await source.read();
          }
          const geojson = { type: "FeatureCollection" as const, features };
          const id = `imported-${Date.now()}-${Math.random()}`;
          addImportedLayer({
            id,
            name: file.name,
            format: "shapefile" as ImportedLayerFormat,
            featureCount: features.length,
            bbox: computeBbox(geojson),
            geojson,
            rawPrj: prjText,
            rawCpg: cpgText,
            rawQmd: qmdText,
            rawShx: shxBuffer,
            isVisible: true,
          });
          return;
        }
        // GeoJSON
        if (
          file.type === "application/json" ||
          file.type === "application/geo+json" ||
          fileNameLower.endsWith(".geojson") ||
          fileNameLower.endsWith(".json")
        ) {
          const parsedGeoJson = parseGeoJsonContent(e.target?.result as string);

          if (!parsedGeoJson.ok) {
            setUploadError(parsedGeoJson.error);
            setFileName(null);
            return;
          }

          const { geojson } = parsedGeoJson;
          const id = `imported-${Date.now()}-${Math.random()}`;
          addImportedLayer({
            id,
            name: file.name,
            format: "geojson" as ImportedLayerFormat,
            featureCount: geojson.features.length,
            bbox: computeBbox(geojson),
            geojson,
            isVisible: true,
          });

          // KML
        } else if (
          file.type === "application/vnd.google-earth.kml+xml" ||
          file.type === "text/xml" ||
          file.type === "application/xml" ||
          fileNameLower.endsWith(".kml")
        ) {
          const kmlResult = parseKmlContent(e.target?.result as string);
          if (!kmlResult.ok) {
            setUploadError(kmlResult.error);
            setFileName(null);
            return;
          }
          const {
            doc: kmlDoc,
            geojson: geojsonMeta,
            safeForPlugin,
          } = kmlResult;
          const id = `imported-${Date.now()}-${Math.random()}`;
          addImportedLayer({
            id,
            name: file.name,
            format: "kml" as ImportedLayerFormat,
            featureCount: geojsonMeta.features.length,
            bbox: computeBbox(geojsonMeta),
            geojson: geojsonMeta,
            rawKml: kmlDoc,
            kmlSafeForPlugin: safeForPlugin,
            isVisible: true,
          });

          // Shapefile
        } else if (fileNameLower.endsWith(".shp")) {
          const stream = await convertFileToStream(file);
          const source = await shapefile.open(stream);
          let features: any[] = [];
          let result = await source.read();
          while (!result.done) {
            features.push(result.value);
            result = await source.read();
          }
          const geojson = { type: "FeatureCollection" as const, features };
          const id = `imported-${Date.now()}-${Math.random()}`;
          addImportedLayer({
            id,
            name: file.name,
            format: "shapefile" as ImportedLayerFormat,
            featureCount: features.length,
            bbox: computeBbox(geojson),
            geojson,
            isVisible: true,
          });

          // Unsupported format
        } else {
          setFileName(null);
          setUploadError(
            "Formato não suportado. Use .kml, .kmz, .json, .geojson, .shp ou .zip"
          );
        }
      } catch (error) {
        console.error("Erro ao processar o arquivo", error);
        setUploadError(
          "Erro ao processar o arquivo. Verifique se o arquivo é válido."
        );
        setFileName(null);
      }
    };

    // Read as array buffer for KMZ and shapefile ZIPs, otherwise as text
    if (isKmz || isZip) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  };

  return (
    <>
      <div className="flex items-center space-x-2">
        {/* 1. The actual input, hidden from view but accessible */}
        <Input
          id="file-upload"
          type="file"
          accept=".geojson, .json, .kml, .kmz, .shp, .zip"
          className="hidden" // Hides the native input element
          onChange={handleFileChange}
        />

        {/* 2. A custom label that acts as your "Choose File" button */}
        <Label
          htmlFor="file-upload"
          className="border border-[#636467] rounded-lg px-2 py-2 w-full justify-center cursor-pointer opacity-90 hover:opacity-100 transition-all flex items-center gap-2 py-2 mx-4 bg-primary"
        >
          {/* Style the label to look like a button */}
          <Button variant="outline" color="#636467" asChild>
            <>
              <span className="text-xs text-white" color="#fff">
                Enviar Arquivo
              </span>
            </>
          </Button>
        </Label>
      </div>
      {uploadError && (
        <p
          style={{
            color: "#D71515",
            fontSize: "12px",
            marginTop: "8px",
            lineHeight: "1.4",
          }}
        >
          {uploadError}
        </p>
      )}
    </>
  );
};
export default FileUpload;
